import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Box,
  Divider,
  InputAdornment,
} from "@mui/material";
import { useState, useMemo } from "react";
import { useCustomMutation } from "@refinedev/core";
import { Order } from "../../../../orders/utils/types";
import { useTranslation } from "../../../../../hooks/useTranslation";
import { useMarketplaceStore } from "../../../../../store/useMarketplaceStore";
import { useQueryClient } from "@tanstack/react-query";
type Props = {
  open: boolean;
  onClose: () => void;
  order: Order;
};

/* =========================
   DECIMAL NORMALIZER
========================= */
const normalizeDecimalInput = (value: string) => {
  let v = value.replaceAll(/,/g, "."); // virgül → nokta
  v = v.replaceAll(/[^0-9.]/g, ""); // sadece rakam + nokta

  const firstDotIndex = v.indexOf(".");
  if (firstDotIndex !== -1) {
    v =
      v.slice(0, firstDotIndex + 1) +
      v.slice(firstDotIndex + 1).replaceAll(/\./g, "");
  }

  return v;
};

export const AlreadyPurchasedDialog = ({ open, onClose, order }: Props) => {
  /* =========================
     ORDER LEVEL STATE
  ========================= */
  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);

  const [amazonSupplyOrderId, setAmazonSupplyOrderId] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [supplyCost, setSupplyCost] = useState<string>("");

  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.orders.detail.sidebar.alreadyPurchased",
  );

  /* =========================
     ITEM LEVEL STATE (STRING!)
  ========================= */
  const [items, setItems] = useState(
    order.orderItems.map((item) => ({
      orderItemId: item.id,
      title: item.title,
      asin: item.asin,
      imageUrl: item.productDetails?.productImageUrl,
      quantityBought: "",
      unitCost: "",
    })),
  );

  const updateItem = (
    index: number,
    field: "quantityBought" | "unitCost",
    value: string,
  ) => {
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
  };

  /* =========================
     VALIDATION
  ========================= */
  const isFormValid = useMemo(() => {
    if (!amazonSupplyOrderId.trim()) return false;
    if (!buyerEmail.trim()) return false;
    if (Number(supplyCost) <= 0) return false;
    if (items.length === 0) return false;

    return items.every(
      (item) => Number(item.quantityBought) >= 1 && Number(item.unitCost) >= 1,
    );
  }, [amazonSupplyOrderId, buyerEmail, supplyCost, items]);

  /* =========================
     MUTATION
  ========================= */
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useCustomMutation();

  /* =========================
     SUBMIT
  ========================= */
  const handleSubmit = () => {
    const finalRequest = {
      amazonSupplyOrderId: amazonSupplyOrderId.trim(),
      buyerEmail: buyerEmail.trim(),
      supplyCost: Number(supplyCost),
      supplyOrderItems: items.map((item) => ({
        orderItemId: item.orderItemId,
        quantity: Number(item.quantityBought),
        unitCost: Number(item.unitCost),
      })),
    };

    console.log("CompleteCheckoutRequest", finalRequest);
    mutate(
      {
        url: `store/${selectedCountry?.id}/order/${order.id}/complete-checkout`,
        method: "put",
        values: finalRequest,
        successNotification: () => ({
          message: translate("completeCheckout.success"),
          type: "success",
        }),
      },
      {
        onSuccess: () => {
          (async () => {
            await queryClient.invalidateQueries({
              queryKey: ["order-detail", selectedCountry?.id, String(order.id)],
            });

            await queryClient.refetchQueries({
              queryKey: ["order-detail", selectedCountry?.id, String(order.id)],
            });

            onClose();
          })();
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{translate("title")}</DialogTitle>

      <DialogContent>
        {/* =========================
            ORDER LEVEL
        ========================= */}
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            {translate("sections.orderInformation")}
          </Typography>

          <TextField
            label={translate("fields.amazonSupplyOrderId")}
            value={amazonSupplyOrderId}
            onChange={(e) => setAmazonSupplyOrderId(e.target.value)}
            required
            fullWidth
          />

          <TextField
            label={translate("fields.buyerEmail")}
            value={buyerEmail}
            onChange={(e) => setBuyerEmail(e.target.value)}
            required
            fullWidth
          />

          <TextField
            label={translate("fields.totalSupplyCost")}
            type="text"
            inputMode="decimal"
            value={supplyCost}
            onChange={(e) =>
              setSupplyCost(normalizeDecimalInput(e.target.value))
            }
            fullWidth
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">$</InputAdornment>,
              },
            }}
          />
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* =========================
            ITEMS
        ========================= */}
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            {translate("sections.purchasedItems")}
          </Typography>

          {items.map((item, index) => (
            <Box
              key={item.orderItemId}
              sx={{
                display: "grid",
                gridTemplateColumns: "80px 1fr",
                gap: 2,
                p: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {/* IMAGE */}
              <Box
                component="img"
                src={item.imageUrl}
                alt={item.asin}
                sx={{
                  width: 80,
                  height: 80,
                  objectFit: "contain",
                  borderRadius: 1,
                }}
              />

              {/* INFO */}
              <Stack spacing={1}>
                <Typography variant="body2" fontWeight={500}>
                  {item.title}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  ASIN: {item.asin}
                </Typography>

                <Stack direction="row" spacing={2}>
                  <TextField
                    label={translate("fields.quantityBought")}
                    type="text"
                    inputMode="numeric"
                    value={item.quantityBought}
                    onChange={(e) =>
                      updateItem(
                        index,
                        "quantityBought",
                        normalizeDecimalInput(e.target.value),
                      )
                    }
                    fullWidth
                  />

                  <TextField
                    label={translate("fields.unitCost")}
                    type="text"
                    inputMode="decimal"
                    value={item.unitCost}
                    onChange={(e) =>
                      updateItem(
                        index,
                        "unitCost",
                        normalizeDecimalInput(e.target.value),
                      )
                    }
                    fullWidth
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">$</InputAdornment>
                        ),
                      },
                    }}
                  />
                </Stack>
              </Stack>
            </Box>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          {translateWithoutPrefix("buttons.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isFormValid || isLoading}
        >
          {translateWithoutPrefix("buttons.approve")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
